<template>
  <Content class="car-realtime-monitor">
    <Card>
      <div slot="title" style="display: flex; align-items: center; justify-content: space-between;">
        <div style="display: flex; align-items: center;">
          <Icon type="ios-pulse" />
          <span style="margin-left: 8px;">
            {{ currentArea ? currentArea.area_name + ' - ' : '' }}车辆实时监控
          </span>
          <Badge 
            :count="alertCount" 
            style="margin-left: 16px;"
            v-if="alertCount > 0"
          >
            <Button size="small" type="error">故障预警</Button>
          </Badge>
        </div>
        <Button 
          v-if="currentArea"
          type="text" 
          icon="ios-exit"
          @click="handleExitArea"
          style="margin-left: auto;"
        >
          退出监控
        </Button>
      </div>

      <!-- 筛选工具栏（仅在列表视图显示） -->
      <div v-if="viewMode === 'list'" class="toolbar" style="margin-bottom: 16px;">
        <Form :model="filterForm" inline class="filter-form">
          <FormItem label="区域">
            <Select 
              v-model="filterForm.area_id" 
              clearable 
              placeholder="全部区域"
              style="width: 150px;"
              @on-change="handleAreaFilterChange"
            >
              <Option 
                v-for="area in areas" 
                :key="area.area_id" 
                :value="area.area_id"
              >
                {{ area.area_name }}
              </Option>
            </Select>
          </FormItem>
          <FormItem label="状态">
            <Select 
              v-model="filterForm.status" 
              clearable 
              placeholder="全部状态"
              style="width: 120px;"
            >
              <Option value="空闲">空闲</Option>
              <Option value="使用中">使用中</Option>
              <Option value="故障">故障</Option>
              <Option value="维护中">维护中</Option>
            </Select>
          </FormItem>
          <FormItem label="在线状态">
            <Select 
              v-model="filterForm.online" 
              clearable 
              placeholder="全部"
              style="width: 100px;"
            >
              <Option :value="true">在线</Option>
              <Option :value="false">离线</Option>
            </Select>
          </FormItem>
          <FormItem label="电量">
            <InputNumber 
              v-model="filterForm.batteryMin" 
              :min="0" 
              :max="100" 
              placeholder="最低"
              style="width: 80px;"
            />
            <span style="margin: 0 8px;">-</span>
            <InputNumber 
              v-model="filterForm.batteryMax" 
              :min="0" 
              :max="100" 
              placeholder="最高"
              style="width: 80px;"
            />
          </FormItem>
          <FormItem label="搜索">
            <Input 
              v-model="filterForm.keyword" 
              placeholder="车辆名称/编号"
              clearable
              style="width: 150px;"
              @on-enter="handleSearch"
            />
          </FormItem>
          <FormItem>
            <Button type="primary" icon="ios-search" @click="handleSearch">查询</Button>
            <Button style="margin-left: 8px;" @click="handleReset">重置</Button>
            <Button 
              style="margin-left: 8px;" 
              :icon="autoRefresh ? 'ios-pause' : 'ios-play'"
              @click="toggleAutoRefresh"
            >
              {{ autoRefresh ? '暂停刷新' : '开始刷新' }}
            </Button>
            <Button 
              style="margin-left: 8px;" 
              icon="ios-refresh"
              @click="handleRefresh"
              :loading="loading"
            >
              手动刷新
            </Button>
          </FormItem>
        </Form>
      </div>

      <!-- 地图和列表切换 -->
      <Tabs v-model="viewMode" style="margin-bottom: 16px;">
        <TabPane label="列表视图" name="list">
          <!-- 列表视图内容 -->
        </TabPane>
        <TabPane label="地图视图" name="map">
          <!-- 地图视图内容 -->
        </TabPane>
      </Tabs>

      <!-- 统计卡片（列表视图显示在Tab下方，和地图视图一致） -->
      <div v-if="viewMode === 'list'" style="display: flex; gap: 8px; margin-bottom: 4px;">
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #1890ff; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statistics.total || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">总车辆数</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #52c41a; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statistics.online || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">在线车辆</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #1890ff; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statistics.inUse || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">使用中</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #ff4d4f; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statistics.alerts || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">故障预警</div>
          </div>
        </Card>
      </div>

      <!-- 统计卡片（地图视图显示在筛选工具栏位置，紧凑布局） -->
      <div v-if="viewMode === 'map'" style="display: flex; gap: 8px; margin-bottom: 4px;">
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #1890ff; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statisticsAll.total || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">总车辆数</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #52c41a; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statisticsAll.online || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">在线车辆</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #1890ff; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statisticsAll.inUse || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">使用中</div>
          </div>
        </Card>
        <Card style="flex: 1; padding: 1px 6px;">
          <div style="text-align: center; padding: 0;">
            <div style="font-size: 20px; font-weight: bold; color: #ff4d4f; margin-bottom: 0; line-height: 1; padding: 0;">
              {{ statisticsAll.alerts || 0 }}
            </div>
            <div style="color: #999; font-size: 12px; line-height: 1; margin-top: 0; padding: 0;">故障预警</div>
          </div>
        </Card>
      </div>

      <!-- 地图视图 -->
      <div v-if="viewMode === 'map'" style="margin-bottom: 16px;">
        <CarMap 
          ref="carMap"
          :cars="mapViewData" 
          :areaBoundaries="areaBoundaries"
          :selectedAreaId="filterForm.area_id"
          :center="mapCenter"
          :zoom="mapZoom"
          @marker-click="handleMapMarkerClick"
          @refresh="handleRefresh"
          @area-select="handleAreaSelect"
        />
      </div>


      <!-- 车辆列表 -->
      <Table
        v-if="viewMode === 'list'"
        :columns="columns"
        :data="filteredTableData"
        :loading="loading"
        border
        stripe
        highlight-row
        @on-row-click="handleRowClick"
      >
        <template slot-scope="{ row }" slot="car_name">
          <div>
            <strong>{{ row.car_name }}</strong>
            <br />
            <span style="color: #999; font-size: 12px;">{{ row.car_ref }}</span>
          </div>
        </template>

        <template slot-scope="{ row }" slot="status">
          <Tag :color="getStatusColor(row.status)">
            {{ row.status || '空闲' }}
          </Tag>
        </template>

        <template slot-scope="{ row }" slot="online">
          <Tag :color="row.online ? 'green' : 'red'">
            {{ row.online ? '在线' : '离线' }}
          </Tag>
          <span 
            v-if="!row.online && getOfflineDuration(row) > 5"
            style="color: #ff4d4f; margin-left: 8px;"
          >
            (离线{{ getOfflineDuration(row) }}分钟)
          </span>
        </template>

        <template slot-scope="{ row }" slot="battery">
          <div style="display: flex; align-items: center;">
            <Progress 
              :percent="row.battery || 0" 
              :status="getBatteryStatus(row.battery)"
              :stroke-width="8"
              style="flex: 1; margin-right: 8px;"
            >
              <span :style="{ color: getBatteryColor(row.battery) }">
                {{ row.battery || 0 }}%
              </span>
            </Progress>
          </div>
          <div 
            v-if="row.battery && row.battery < 20" 
            style="color: #ff4d4f; font-size: 12px; margin-top: 4px;"
          >
            ⚠️ 低电量预警
          </div>
        </template>

        <template slot-scope="{ row }" slot="location">
          <div>
            <Icon type="ios-locate" />
            <span>{{ getAreaName(row.area_id) || row.area_name || '未知区域' }}</span>
          </div>
          <div 
            v-if="row.location_address" 
            style="color: #999; font-size: 12px; margin-top: 4px;"
          >
            {{ row.location_address }}
          </div>
        </template>

        <template slot-scope="{ row }" slot="alerts">
          <div v-if="getAlerts(row).length > 0">
            <Tag 
              v-for="alert in getAlerts(row)" 
              :key="alert.type"
              :color="alert.color"
              style="margin-bottom: 4px;"
            >
              {{ alert.text }}
            </Tag>
          </div>
          <span v-else style="color: #999;">正常</span>
        </template>

        <template slot-scope="{ row }" slot="in_area">
          <Tag :color="row.in_area ? 'green' : 'red'">
            {{ row.in_area ? '在区域内' : '越界' }}
          </Tag>
        </template>

        <template slot-scope="{ row }" slot="action">
          <Button 
            type="primary" 
            size="small" 
            @click.stop="viewDetail(row)"
          >
            详情
          </Button>
          <Button 
            type="info" 
            size="small" 
            style="margin-left: 8px;"
            @click.stop="testConnect(row)"
          >
            测试连接
          </Button>
        </template>
      </Table>

      <!-- 分页 -->
      <Page
        v-show="total > 0"
        :total="total"
        :current="pageInfo.pageNo"
        :page-size="pageInfo.pageSize"
        :page-size-opts="[10, 20, 50, 100]"
        @on-change="handlePageChange"
        @on-page-size-change="handlePageSizeChange"
        show-sizer
        show-elevator
        show-total
        style="margin-top: 16px; text-align: right;"
      />
    </Card>

    <!-- 车辆详情弹窗 -->
    <Modal
      v-model="detailModalVisible"
      title="车辆详情"
      width="800"
      :mask-closable="false"
    >
      <div v-if="selectedCar">
        <Descriptions :column="2" border>
          <DescriptionsItem label="车辆名称">{{ selectedCar.car_name }}</DescriptionsItem>
          <DescriptionsItem label="车辆编号">{{ selectedCar.car_ref }}</DescriptionsItem>
          <DescriptionsItem label="所属区域">{{ selectedCar.area_name }}</DescriptionsItem>
          <DescriptionsItem label="计费价格">¥{{ selectedCar.car_price }}/小时</DescriptionsItem>
          <DescriptionsItem label="车辆状态">
            <Tag :color="getStatusColor(selectedCar.status)">
              {{ selectedCar.status || '空闲' }}
            </Tag>
          </DescriptionsItem>
          <DescriptionsItem label="在线状态">
            <Tag :color="selectedCar.online ? 'green' : 'red'">
              {{ selectedCar.online ? '在线' : '离线' }}
            </Tag>
          </DescriptionsItem>
          <DescriptionsItem label="当前电量">
            <Progress 
              :percent="selectedCar.battery || 0" 
              :status="getBatteryStatus(selectedCar.battery)"
            />
            {{ selectedCar.battery || 0 }}%
          </DescriptionsItem>
          <DescriptionsItem label="MQTT连接">
            <Tag :color="selectedCar.mqtt_connected ? 'green' : 'red'">
              {{ selectedCar.mqtt_connected ? '已连接' : '未连接' }}
            </Tag>
          </DescriptionsItem>
          <DescriptionsItem label="当前位置" :span="2">
            {{ selectedCar.location_address || '未知' }}
          </DescriptionsItem>
          <DescriptionsItem label="最后更新时间" :span="2">
            {{ formatTime(selectedCar.last_update_time) }}
          </DescriptionsItem>
        </Descriptions>
      </div>
      <div slot="footer">
        <Button @click="detailModalVisible = false">关闭</Button>
      </div>
    </Modal>
  </Content>
</template>

<script>
import { getAllCarsRealTimeStatus, CarTryConnect } from '@/api/car'
import { getAreaBoundaries } from '@/api/area'
import CarMap from '@/components/CarMap.vue'

export default {
  name: 'CarRealTimeMonitor',
  components: {
    CarMap
  },
  data() {
    return {
      loading: false,
      autoRefresh: true,
      refreshTimer: null,
      refreshInterval: 30000, // 30秒刷新一次
      
      viewMode: 'list', // 'list' 或 'map'
      mapCenter: [116.397428, 39.90923], // 默认北京
      mapZoom: 13,
      
      areas: [],
      areaBoundaries: [], // 区域边界数据
      currentArea: null, // 当前查看的区域信息
      filterForm: {
        area_id: '',
        status: '',
        online: null,
        batteryMin: null,
        batteryMax: null,
        keyword: ''
      },
      
      tableData: [],
      pageInfo: {
        pageNo: 1,
        pageSize: 20
      },
      
      columns: [
        {
          title: '车辆信息',
          slot: 'car_name',
          width: 200,
          fixed: 'left'
        },
        {
          title: '状态',
          slot: 'status',
          width: 100,
          align: 'center'
        },
        {
          title: '在线状态',
          slot: 'online',
          width: 150,
          align: 'center'
        },
        {
          title: '电量',
          slot: 'battery',
          width: 200,
          align: 'center'
        },
        {
          title: '位置',
          slot: 'location',
          minWidth: 200,
          width: 200
        },
        {
          title: '区域状态',
          slot: 'in_area',
          width: 120,
          align: 'center'
        },
        {
          title: '预警信息',
          slot: 'alerts',
          minWidth: 200,
          width: 200
        },
        {
          title: '最后更新',
          key: 'last_update_time',
          width: 180,
          render: (h, params) => {
            return h('span', this.formatTime(params.row.last_update_time))
          }
        },
        {
          title: '操作',
          slot: 'action',
          width: 180,
          align: 'center'
        }
      ],
      
      detailModalVisible: false,
      selectedCar: null
    }
  },
  
  computed: {
    // 预警数量（基于统计信息）
    alertCount() {
      return this.statistics.alerts
    },
    // 筛选后的全部数据（不分页），用于统计计算
    filteredAllData() {
      let data = [...this.tableData]
      
      // 区域筛选
      if (this.filterForm.area_id) {
        // 确保类型匹配（都转换为数字进行比较）
        const filterAreaId = Number(this.filterForm.area_id)
        data = data.filter(item => {
          const itemAreaId = Number(item.area_id)
          return itemAreaId === filterAreaId
        })
      }
      
      // 状态筛选
      if (this.filterForm.status) {
        data = data.filter(item => item.status === this.filterForm.status)
      }
      
      // 在线状态筛选
      if (this.filterForm.online !== null) {
        data = data.filter(item => item.online === this.filterForm.online)
      }
      
      // 电量范围筛选
      if (this.filterForm.batteryMin !== null) {
        data = data.filter(item => (item.battery || 0) >= this.filterForm.batteryMin)
      }
      if (this.filterForm.batteryMax !== null) {
        data = data.filter(item => (item.battery || 0) <= this.filterForm.batteryMax)
      }
      
      // 关键词搜索
      if (this.filterForm.keyword) {
        const keyword = this.filterForm.keyword.toLowerCase()
        data = data.filter(item => 
          (item.car_name && item.car_name.toLowerCase().includes(keyword)) ||
          (item.car_ref && item.car_ref.toLowerCase().includes(keyword))
        )
      }
      
      return data
    },
    
    // 分页后的数据，用于表格显示
    filteredTableData() {
      const start = (this.pageInfo.pageNo - 1) * this.pageInfo.pageSize
      const end = start + this.pageInfo.pageSize
      return this.filteredAllData.slice(start, end)
    },
    
    // 统计信息（基于筛选后的全部数据，用于列表视图）
    statistics() {
      const data = this.filteredAllData
      return {
        total: data.length,
        online: data.filter(car => car.online).length,
        inUse: data.filter(car => car.status === '使用中').length,
        alerts: data.filter(car => this.getAlerts(car).length > 0).length
      }
    },
    
    // 统计信息（基于区域ID过滤后的数据，用于地图视图）
    statisticsAll() {
      let data = [...this.tableData]
      
      // 根据区域ID过滤
      if (this.filterForm.area_id) {
        const filterAreaId = Number(this.filterForm.area_id)
        data = data.filter(item => {
          const itemAreaId = Number(item.area_id)
          return itemAreaId === filterAreaId
        })
      }
      
      return {
        total: data.length,
        online: data.filter(car => car.online).length,
        inUse: data.filter(car => car.status === '使用中').length,
        alerts: data.filter(car => this.getAlerts(car).length > 0).length
      }
    },
    
    // 分页总数（基于筛选后的全部数据）
    total() {
      return this.filteredAllData.length
    },
    
    // 地图视图使用的数据（根据区域ID过滤）
    mapViewData() {
      let data = [...this.tableData]
      
      // 根据区域ID过滤
      if (this.filterForm.area_id) {
        const filterAreaId = Number(this.filterForm.area_id)
        data = data.filter(item => {
          const itemAreaId = Number(item.area_id)
          return itemAreaId === filterAreaId
        })
      }
      
      return data
    }
  },
  
  mounted() {
    // 从路由参数初始化（必须在最前面）
    this.initFromRoute()
    this.loadAreas()
    this.loadAreaBoundaries()
    this.loadData()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  
  watch: {
    // 监听路由变化
    '$route'(to, from) {
      if (to.name === 'carRealtime' && to.query.area_id !== from.query.area_id) {
        this.initFromRoute()
        this.loadData()
      }
    },
    // 监听视图模式变化
    viewMode(newMode, oldMode) {
      // 当切换到地图视图时，自动适应区域边界
      if (newMode === 'map' && oldMode !== 'map') {
        this.$nextTick(() => {
          if (this.$refs.carMap && this.filterForm.area_id) {
            // 重置适应标记并触发适应
            this.$refs.carMap.hasFittedToArea = false
            this.$refs.carMap.fitToSelectedArea()
          }
        })
      }
    }
  },
  
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  
  methods: {
    // 从路由参数初始化
    initFromRoute() {
      const areaId = this.$route.query.area_id
      if (areaId) {
        const areaIdNum = typeof areaId === 'string' ? parseInt(areaId, 10) : areaId
        console.log('从路由获取区域ID:', areaIdNum)
        
        // 设置筛选条件
        this.filterForm.area_id = areaIdNum
        
        // 从区域列表中查找当前区域信息
        this.setCurrentArea(areaIdNum)
        
        // 如果是从区域进入，默认显示地图视图
        this.viewMode = 'map'
      } else {
        // 如果没有区域参数，清除当前区域
        this.currentArea = null
        this.filterForm.area_id = ''
      }
    },
    
    // 设置当前区域信息
    setCurrentArea(areaId) {
      try {
        const areasStr = this.$store.state.areas
        if (areasStr) {
          const areas = JSON.parse(areasStr)
          const area = areas.find(a => a.area_id === areaId || a.areaId === areaId)
          if (area) {
            const centerCoords = (area.center && area.center.coordinates)
              ? area.center.coordinates
              : (area.location && area.location.coordinates)
                ? area.location.coordinates
                : null
            this.currentArea = {
              area_id: area.area_id || area.areaId,
              area_name: area.area_name || area.areaName,
              area_desc: area.area_desc || area.areaDesc,
              centerCoords
            }
            console.log('设置当前区域:', this.currentArea)
            
            // 如果区域有中心点，设置地图中心
            if (centerCoords && centerCoords.length === 2) {
              this.mapCenter = centerCoords
            }
          } else {
            console.warn('未找到区域信息，areaId:', areaId)
            this.currentArea = null
          }
        }
      } catch (error) {
        console.error('设置当前区域失败:', error)
        this.currentArea = null
      }
    },
    
    // 退出监控，返回车辆管理界面
    handleExitArea() {
      // 跳转到车辆管理界面
      this.$router.push({ 
        name: 'car'
      })
    },
    
    // 加载区域列表
    loadAreas() {
      try {
        const areasStr = this.$store.state.areas
        if (areasStr) {
          const areaList = JSON.parse(areasStr)
          
          // 修复错误的location字段
          this.areas = areaList.map(area => {
            // 检查location字段的坐标是否有效
            if (area.location && area.location.coordinates) {
              const [lng, lat] = area.location.coordinates
              // 如果坐标超出有效范围，使用center字段的值
              if (lng < -180 || lng > 180 || lat < -90 || lat > 90) {
                console.warn(`区域 ${area.area_name} (ID: ${area.area_id}) 的location坐标无效: [${lng}, ${lat}]，使用center字段的值`)
                if (area.center && area.center.coordinates) {
                  area.location = {
                    type: 'Point',
                    coordinates: area.center.coordinates
                  }
                } else {
                  // 如果center也没有，删除location字段
                  delete area.location
                }
              }
            }
            return area
          })
          
          // 如果已经有当前区域ID，但还没有区域信息，重新设置
          if (this.filterForm.area_id && !this.currentArea) {
            this.setCurrentArea(this.filterForm.area_id)
          }
        }
      } catch (error) {
        console.error('加载区域列表失败:', error)
      }
    },

    // 加载区域边界数据
    async loadAreaBoundaries() {
      try {
        const res = await getAreaBoundaries()
        if (res.data.code === 200) {
          const boundaries = res.data.data || []
          console.log('原始区域边界数据:', JSON.parse(JSON.stringify(boundaries)))
          console.log('第一个区域的完整数据:', JSON.parse(JSON.stringify(boundaries[0] || {})))
          this.areaBoundaries = boundaries.map((item, index) => {
            console.log(`处理第 ${index + 1} 个区域:`, {
              area_id: item.area_id,
              area_name: item.area_name,
              boundary: item.boundary,
              boundaryType: typeof item.boundary,
              boundaryIsArray: Array.isArray(item.boundary),
              boundaryIsNull: item.boundary === null,
              boundaryIsUndefined: item.boundary === undefined
            })
            const boundaryPoints = this.normalizeBoundaryData(item.boundary)
            const center = this.normalizeCenter(item.center)
            return {
              area_id: item.area_id || item.areaId,
              area_name: item.area_name || item.areaName,
              area_desc: item.area_desc || item.areaDesc,
              boundary: boundaryPoints,
              center,
              bounds: item.bounds || null
            }
          })
          console.log('加载区域边界:', JSON.parse(JSON.stringify(this.areaBoundaries)))
        } else {
          console.error('加载区域边界失败:', res.data.msg)
        }
      } catch (error) {
        console.error('加载区域边界失败:', error)
      }
    },

    // 规范化边界坐标
    normalizeBoundaryData(boundary) {
      let points = []
      if (!boundary) {
        console.log('normalizeBoundaryData: boundary 为空')
        return points
      }

      console.log('normalizeBoundaryData 输入:', typeof boundary, Array.isArray(boundary), 
        Array.isArray(boundary) && boundary.length > 0 ? 
          (Array.isArray(boundary[0]) ? (Array.isArray(boundary[0][0]) ? '三维数组' : '二维数组') : '一维数组') : '非数组',
        boundary)

      if (typeof boundary === 'string') {
        try {
          boundary = JSON.parse(boundary)
        } catch (error) {
          console.warn('边界字符串解析失败:', boundary)
          return points
        }
      }

      if (Array.isArray(boundary)) {
        // 检查是否是三维数组 [[[lng, lat], ...]]
        if (boundary.length > 0 && Array.isArray(boundary[0]) && Array.isArray(boundary[0][0])) {
          // 三维数组，取第一层
          points = boundary[0]
          console.log('normalizeBoundaryData: 检测到三维数组，取第一层:', points.length, '个点')
        } else {
          // 二维数组 [[lng, lat], ...]
          points = boundary
          console.log('normalizeBoundaryData: 检测到二维数组:', points.length, '个点')
        }
      } else if (boundary.coordinates) {
        if (Array.isArray(boundary.coordinates[0]) && Array.isArray(boundary.coordinates[0][0])) {
          // 三维数组 [[[lng, lat], ...]]
          points = boundary.coordinates[0]
          console.log('normalizeBoundaryData: 从 coordinates[0] 提取:', points.length, '个点')
        } else if (Array.isArray(boundary.coordinates)) {
          points = boundary.coordinates
          console.log('normalizeBoundaryData: 从 coordinates 提取:', points.length, '个点')
        }
      } else if (Array.isArray(boundary.points)) {
        points = boundary.points
        console.log('normalizeBoundaryData: 从 points 提取:', points.length, '个点')
      }

      const validPoints = points
        .filter(point => Array.isArray(point) && point.length >= 2)
        .map(point => {
          const lng = Number(point[0])
          const lat = Number(point[1])
          return [lng, lat]
        })
        .filter(point => !point.some(val => Number.isNaN(val)))
      
      console.log('normalizeBoundaryData 输出:', validPoints.length, '个有效点', validPoints.slice(0, 3))
      return validPoints
    },

    // 规范化中心点
    normalizeCenter(center) {
      if (!center) return null
      if (Array.isArray(center)) {
        return center
      }
      if (center.coordinates && Array.isArray(center.coordinates)) {
        return center.coordinates
      }
      if (center.lng !== undefined && center.lat !== undefined) {
        return [center.lng, center.lat]
      }
      return null
    },

    // 处理区域选择（从地图信息窗口点击"选择此区域"按钮触发）
    handleAreaSelect(area) {
      console.log('handleAreaSelect 被调用，区域信息:', area)
      if (area && area.area_id) {
        // 设置区域ID
        this.filterForm.area_id = area.area_id
        // 调用区域筛选变化处理，这会设置当前区域、更新路由、更新地图中心点等
        this.handleAreaFilterChange(area.area_id)
        // 刷新数据
        this.loadData()
        this.$Message.success(`已切换到区域：${area.area_name || area.areaName || '未知区域'}`)
      } else {
        console.warn('handleAreaSelect: 区域信息无效', area)
        this.$Message.warning('区域信息无效，无法切换')
      }
    },
    
    // 根据区域ID获取区域名称
    getAreaName(areaId) {
      if (!areaId) return ''
      try {
        const areaIdNum = Number(areaId)
        const area = this.areas.find(a => 
          (a.area_id === areaIdNum) || 
          (a.area_id === areaId) || 
          (a.areaId === areaIdNum) || 
          (a.areaId === areaId)
        )
        return area ? (area.area_name || area.areaName || '') : ''
      } catch (error) {
        console.error('获取区域名称失败:', error)
        return ''
      }
    },
    
    // 处理区域筛选变化
    handleAreaFilterChange(areaId) {
      if (areaId) {
        // 如果选择了区域，设置当前区域信息
        this.setCurrentArea(areaId)
        // 更新路由参数（只有当路由参数不同时才更新，避免重复导航错误）
        const currentAreaId = this.$route.query.area_id
        const areaIdStr = String(areaId)
        if (currentAreaId !== areaIdStr) {
          this.$router.replace({
            name: 'carRealtime',
            query: { area_id: areaId }
          }).catch(err => {
            // 捕获重复导航错误（这是正常的，不影响功能）
            if (err.name !== 'NavigationDuplicated') {
              console.error('路由导航错误:', err)
            }
          })
        }
      } else {
        // 如果清空了区域，退出区域监控
        this.handleExitArea()
      }
    },
    
    // 加载车辆实时数据
    async loadData() {
      this.loading = true
      try {
        const res = await getAllCarsRealTimeStatus({
          ...this.pageInfo
        })
        
        if (res.data.code === 200) {
          this.tableData = res.data.data || []
          
          // 如果有车辆数据，更新地图中心点
          if (this.tableData.length > 0 && this.viewMode === 'map') {
            this.updateMapCenter()
          }
        } else {
          this.$Message.error(res.data.msg || '加载数据失败')
        }
      } catch (error) {
        console.error('加载车辆实时数据失败:', error)
        this.$Message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    // 更新地图中心点
    updateMapCenter() {
      // 如果当前选择了区域，优先使用区域中心点
      if (this.currentArea && this.currentArea.centerCoords && this.currentArea.centerCoords.length === 2) {
        this.mapCenter = this.currentArea.centerCoords
        return
      }
      
      const carsWithLocation = this.tableData.filter(car => 
        car.location && 
        car.location.coordinates && 
        car.location.coordinates.length === 2
      )
      
      if (carsWithLocation.length > 0) {
        // 计算所有车辆的中心点
        const lngs = carsWithLocation.map(c => c.location.coordinates[0])
        const lats = carsWithLocation.map(c => c.location.coordinates[1])
        const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2
        const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2
        this.mapCenter = [centerLng, centerLat]
      }
    },
    
    // 获取车辆预警信息
    getAlerts(car) {
      const alerts = []
      
      // 区域越界预警
      if (car.in_area === false) {
        alerts.push({
          type: 'area_violation',
          text: '区域越界',
          color: 'error'
        })
      }
      
      // 离线超时预警
      if (!car.online && this.getOfflineDuration(car) > 5) {
        alerts.push({
          type: 'offline',
          text: '离线超时',
          color: 'error'
        })
      }
      
      // 低电量预警
      if (car.battery !== null && car.battery < 20) {
        alerts.push({
          type: 'battery',
          text: car.battery < 10 ? '电量极低' : '电量不足',
          color: 'error'
        })
      }
      
      // MQTT连接失败预警
      if (car.online && !car.mqtt_connected) {
        alerts.push({
          type: 'mqtt',
          text: 'MQTT未连接',
          color: 'warning'
        })
      }
      
      // 故障状态预警
      if (car.status === '故障') {
        alerts.push({
          type: 'fault',
          text: '车辆故障',
          color: 'error'
        })
      }
      
      return alerts
    },
    
    // 获取离线时长（分钟）
    getOfflineDuration(car) {
      if (car.online || !car.last_update_time) return 0
      const now = new Date()
      const lastUpdate = new Date(car.last_update_time)
      const diff = (now - lastUpdate) / 1000 / 60 // 转换为分钟
      return Math.floor(diff)
    },
    
    // 获取状态颜色
    getStatusColor(status) {
      const colorMap = {
        '空闲': 'green',
        '使用中': 'blue',
        '故障': 'red',
        '维护中': 'yellow'
      }
      return colorMap[status] || 'default'
    },
    
    // 获取电量状态
    getBatteryStatus(battery) {
      if (battery === null || battery === undefined) return 'exception'
      if (battery > 50) return 'success'
      if (battery > 20) return 'active'
      return 'exception'
    },
    
    // 获取电量颜色
    getBatteryColor(battery) {
      if (battery === null || battery === undefined) return '#999'
      if (battery > 50) return '#52c41a'
      if (battery > 20) return '#faad14'
      return '#ff4d4f'
    },
    
    // 格式化时间
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      return date.toLocaleString('zh-CN')
    },
    
    // 搜索
    handleSearch() {
      this.pageInfo.pageNo = 1
      // 这里不需要重新加载数据，因为使用了computed过滤
    },
    
    // 重置
    handleReset() {
      this.filterForm = {
        area_id: '',
        status: '',
        online: null,
        batteryMin: null,
        batteryMax: null,
        keyword: ''
      }
      this.pageInfo.pageNo = 1
    },
    
    // 刷新
    handleRefresh() {
      this.loadData()
    },
    
    // 切换自动刷新
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh
      if (this.autoRefresh) {
        this.startAutoRefresh()
        this.$Message.success('已开启自动刷新')
      } else {
        this.stopAutoRefresh()
        this.$Message.info('已关闭自动刷新')
      }
    },
    
    // 开始自动刷新
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(() => {
        this.loadData()
      }, this.refreshInterval)
    },
    
    // 停止自动刷新
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    
    // 分页改变
    handlePageChange(page) {
      this.pageInfo.pageNo = page
    },
    
    // 每页数量改变
    handlePageSizeChange(size) {
      this.pageInfo.pageSize = size
      this.pageInfo.pageNo = 1
    },
    
    // 行点击
    handleRowClick(row) {
      // 可以在这里添加行点击事件
    },
    
    // 地图标记点击
    handleMapMarkerClick(car) {
      this.viewDetail(car)
    },
    
    // 查看详情
    viewDetail(car) {
      this.selectedCar = car
      this.detailModalVisible = true
    },
    
    // 测试连接
    async testConnect(car) {
      this.$Modal.confirm({
        title: '测试连接',
        content: `确定要测试车辆"${car.car_name}"的连接吗？`,
        onOk: async () => {
          try {
            const res = await CarTryConnect(car)
            if (res.data.code === 200) {
              this.$Message.success('连接测试成功')
              this.loadData()
            } else {
              this.$Message.error(res.data.msg || '连接测试失败')
            }
          } catch (error) {
            this.$Message.error('连接测试失败')
          }
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.car-realtime-monitor {
  .toolbar {
    background: #f5f5f5;
    padding: 16px;
    border-radius: 4px;
  }
  
  // 筛选表单布局优化
  .filter-form {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    
    /deep/ .ivu-form-item {
      margin-right: 16px;
      margin-bottom: 0;
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      
      .ivu-form-item-label {
        padding-right: 8px;
        white-space: nowrap;
      }
      
      .ivu-form-item-content {
        display: inline-flex;
        align-items: center;
      }
    }
  }
  
  /deep/ .ivu-table-row {
    cursor: pointer;
    
    &:hover {
      background-color: #f5f5f5;
    }
  }
  
  /deep/ .ivu-progress {
    .ivu-progress-bg {
      transition: all 0.3s;
    }
  }
}
</style>

